/**
 * FO-20 / QA-08: reload the app without throwing away work in progress.
 *
 * Two things reload the tab on their own: a new service worker taking control
 * (any open tab that accepts "New version available" makes the new worker
 * control every tab, and each one reloads) and a lazy chunk that 404s after a
 * deploy. Both used to call location.reload() at once, so a clerk typing a
 * payment or a supplier bill saw the page snap back to the list with the
 * dialog gone and no message.
 *
 * reloadWhenIdle() reloads at once when nothing is in progress. While a modal
 * dialog is open or focus is in a field it waits, tells the user once, and
 * reloads only after the work surface has been quiet for IDLE_MS.
 */

const CHECK_EVERY_MS = 1_000
const IDLE_MS = 3_000

export interface BusyDoc {
  querySelector: (selector: string) => unknown
  activeElement: { tagName?: string; isContentEditable?: boolean } | null
}

/** True while the user is mid-task: a modal dialog is open or a field has focus. */
export function isUserBusy(doc: BusyDoc): boolean {
  if (doc.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]')) return true
  const el = doc.activeElement
  if (!el) return false
  if (el.isContentEditable) return true
  const tag = (el.tagName ?? '').toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

export interface ReloadDeps {
  isBusy: () => boolean
  reload: () => void
  notify: (message: string) => void
  setInterval: (fn: () => void, ms: number) => number
  clearInterval: (id: number) => void
  now: () => number
}

let pending: number | null = null

/** Test hook: forget a scheduled reload. */
export function _resetSafeReload(deps?: Pick<ReloadDeps, 'clearInterval'>): void {
  if (pending != null) deps?.clearInterval(pending)
  pending = null
}

export function reloadWhenIdle(deps: ReloadDeps): 'now' | 'deferred' | 'already-pending' {
  if (pending != null) return 'already-pending'
  if (!deps.isBusy()) {
    deps.reload()
    return 'now'
  }
  deps.notify(
    'A new version of Basiyo is ready. The page will refresh by itself once you finish what you are doing.',
  )
  let quietSince: number | null = null
  pending = deps.setInterval(() => {
    if (deps.isBusy()) {
      quietSince = null
      return
    }
    const t = deps.now()
    if (quietSince == null) quietSince = t
    if (t - quietSince >= IDLE_MS) {
      if (pending != null) deps.clearInterval(pending)
      pending = null
      deps.reload()
    }
  }, CHECK_EVERY_MS)
  return 'deferred'
}

/** Browser wiring: real document, location and toast. */
export function browserReloadDeps(notify: (message: string) => void): ReloadDeps {
  return {
    isBusy: () => isUserBusy(document),
    reload: () => window.location.reload(),
    notify,
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id),
    now: () => Date.now(),
  }
}
